import FormStatusBadge from 'components/badge/FormStatusBadge';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { DivConst } from 'constants/divConst';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import WsModalReportDownload from 'pages/ws/components/modalReportDownload/WsModalReportDownload';
import WsModalReportTerm from 'pages/ws/components/modalReportTerm/WsModalReportTerm';
import WsReportBehaviorAnalytics from 'pages/ws/components/reportBehaviorAnalytics/WsReportBehaviorAnalytics';
import WsReportChart from 'pages/ws/components/reportChart/WsReportChart';
import WsReportDetailHeader from 'pages/ws/components/reportDetailHeader/WsReportDetailHeader';
import WsReportTermSection from 'pages/ws/components/reportTermSection/WsReportTermSection';
import WsReportMetrics from 'pages/ws/components/reportMetrics/WsReportMetrics';
import { useWsReportForm } from './hooks/useWsReportForm';

import type { JSX } from 'react';

/**
 * フォームレポート詳細画面
 */
export default function WsReportForm(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const navigate = useNavigate();
  const { formCd } = useParams();

  const [acctState] = useAtom(AcctState);

  const {
    initialized,
    processing,
    form,
    reportDateFrom,
    reportDateTo,
    isExpiredPlan,
    modalTermOpen,
    setModalTermOpen,
    modalDownloadOpen,
    setModalDownloadOpen,
    onContentOpen,
    onModalReportDownloadOpen,
    onModalReportTermOpen,
    onChangeReportTerm,
  } = useWsReportForm(formCd, acctState.company);

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* 初期化中の表示 */}
      <IndicatorScreen visible={!initialized || processing} />
      {/* 初期化後の表示 */}
      {initialized && form && (
        <>
          <WsReportDetailHeader
            imageKey={form.imageKey}
            title={form.title}
            createdAt={form.createdAt}
            statusBadge={<FormStatusBadge formStatusDiv={form.formStatusDiv} />}
            isExpiredPlan={isExpiredPlan}
            onBack={() => navigate(-1)}
            onContentOpen={onContentOpen}
            onModalReportDownloadOpen={onModalReportDownloadOpen}
          />

          <WsReportTermSection
            reportDateFrom={reportDateFrom}
            reportDateTo={reportDateTo}
            onModalReportTermOpen={onModalReportTermOpen}
          />

          <WsReportMetrics
            participatedCount={form.participatedCount}
            lineFlag={form.lineFlag}
            mailAddressCount={form.mailAddressCount}
            lineCount={form.lineCount}
            mailAddressAcquisitionRate={form.mailAddressAcquisitionRate}
            lineAddressAcquisitionRate={form.lineAddressAcquisitionRate}
            answeredCount={form.answeredCount}
            answeredRate={form.answeredRate}
          />

          {/* レポートチャート */}
          <WsReportChart reports={form.reports} lineFlag={form.lineFlag} />

          <WsReportBehaviorAnalytics behaviorAnalytics={form.behaviorAnalytics} />
        </>
      )}

      {/* レポート期間設定モーダル */}
      <WsModalReportTerm
        open={modalTermOpen}
        setOpen={setModalTermOpen}
        reportDateFrom={reportDateFrom}
        reportDateTo={reportDateTo}
        onSubmit={attr => onChangeReportTerm(attr.reportDateFrom, attr.reportDateTo)}
      />
      {/* レポートダウンロードモーダル */}
      <WsModalReportDownload
        open={modalDownloadOpen}
        setOpen={setModalDownloadOpen}
        cd={formCd}
        contentsType={DivConst.items.contentsType.form.value}
        reportDateFrom={null}
        reportDateTo={null}
      />
      {
        // プラン（利用期間切れ）の場合に表示する
        isExpiredPlan && <Tooltip id="custom-tooltip" place="top" variant="info" />
      }
    </>
  );
}
